// Light ITFM assessment - one representative question per dimension, extracted from the
// full gated questionnaire. Content to be reviewed/refined later.

export const GUIDE = {
  name: "Marc Hesse",
  role: "Enterprise Value Consulting",
  photo:
    "https://customer-assets.emergentagent.com/job_marcx-preview-access/artifacts/v15sbrfp_TBM%20Amsterdam%20%281080%20x%201080%20px%29%20%281%29.jpg",
  linkedin: "https://www.linkedin.com/in/marchesse/",
  whatsappNumber: "4915122947600",
};

export const SEATS = [
  {
    id: "business",
    dept: "The consumer",
    role: "Business",
    desc:
      "You run a unit that pays the IT bill. You want to know what you're getting for what you pay, and whether anyone can explain it without 14 tabs.",
  },
  {
    id: "it",
    dept: "The provider",
    role: "Information Technology",
    desc:
      "You own delivery, services, and operations. You assess from the supply side: how transparently and efficiently your function actually provides what it bills for.",
  },
  {
    id: "finance",
    dept: "The steward",
    role: "Finance",
    desc:
      "You own budgeting, planning, and financial reporting for technology. You assess how well the numbers hold up: to audit, to the board, and to the unit economics.",
  },
];

export const LEVELS = [
  {
    idx: 0, code: "L1", name: "Foundational", archetype: "Reactive spender", min: 0.0,
    desc: "The bill arrives, you pay it. Costs show up through GL accounts, not through services, business consumption, or a shared vocabulary. Foundations come first: without a single source of financial truth and a first cost model, no other cost-management capability holds.",
  },
  {
    idx: 1, code: "L2", name: "Emerging", archetype: "Cost-aware operator", min: 1.67,
    desc: "Basic allocation works, you can answer what things cost. The focus now is converting visibility into action: building a proper service cost model, launching first show-back reports, and starting a shared financial vocabulary between IT and Finance.",
  },
  {
    idx: 2, code: "L3", name: "Defined", archetype: "Value translator", min: 2.67,
    desc: "IT speaks the business's language. Spend is traceable to services and business outcomes, consumption-based show-back is live, and cross-functional dialogue is routine. You are moving from visibility to strategic alignment.",
  },
  {
    idx: 3, code: "L4", name: "Managed", archetype: "Accountable steward", min: 3.67,
    desc: "Unit economics, benchmark-informed targets, active chargeback, and meaningful business-IT financial dialogue are operational. Both sides of the bill are accountable; continuous improvement and demand shaping are the next horizon.",
  },
  {
    idx: 4, code: "L5", name: "Leading", archetype: "Enterprise orchestrator", min: 4.67,
    desc: "Technology spend is a board-level strategic lever. CIO and CFO share one number. Value is measured in business KPIs, continuous optimisation is business-as-usual, and a shared financial language enables strategic dialogue at every level.",
  },
];

export const levelForScore = (score) => {
  let chosen = LEVELS[0];
  for (const lvl of LEVELS) if (score >= lvl.min) chosen = lvl;
  return chosen;
};

// Pick the seat-specific phrasing, falling back to the neutral default.
export const questionText = (q, persona) => (persona && q.text[persona]) || q.text.default;

export const DIMENSIONS = [
  {
    code: "D1",
    label: "Foundations",
    sub: "Data · tools · methods · roles · change · skills · strategy",
    text: {
      business:
        "When you ask IT for financial information, how quickly and accurately can they actually provide it?",
      it:
        "How well is your financial and operational data connected and automated across your technology organisation?",
      finance:
        "How is IT financial data collected, integrated, and validated across your organisation's source systems?",
      default:
        "How is IT financial data currently collected and integrated across your organisation?",
    },
    options: [
      { level: "L1", text: "Data is extracted manually and ad-hoc from the GL; takes days; quality is unreliable" },
      { level: "L2", text: "GL is the primary source; other systems (CMDB, HR, procurement) are identified but not yet connected" },
      { level: "L3", text: "Multiple sources connected via automated feeds; manual extraction largely eliminated" },
      { level: "L4", text: "All material sources connected in near-real-time; data lineage and audit trail fully documented" },
      { level: "L5", text: "Data model continuously refreshed by autonomous AI agents; predictive signals and external benchmarks integrated live; anomalies detected and reconciled without human intervention" },
    ],
  },
  {
    code: "D2",
    label: "Cost model & taxonomy",
    sub: "Allocation coverage · cost taxonomy · consumption drivers · TCO · cloud · federated spend",
    text: {
      business:
        "What proportion of the total IT bill is broken down and attributed to specific business units or services, versus sitting in a shared, unallocated pool?",
      it:
        "What percentage of your total IT spend is currently allocated from the GL to a consuming entity (business unit, service, or product)?",
      finance:
        "What percentage of total IT expenditure is formally allocated from the general ledger to a defined cost-bearing entity?",
      default:
        "What percentage of your total IT spend is currently allocated from the GL to a consuming entity?",
    },
    options: [
      { level: "L1", text: "Less than 30%: most spend sits unallocated in shared pools or cost centres" },
      { level: "L2", text: "30-60%: major categories allocated; shared costs remain largely undistributed" },
      { level: "L3", text: "60-85%: comprehensive allocation; automated for primary spend categories" },
      { level: "L4", text: "85-95%: near-complete allocation; cloud commitments and intercompany charges included" },
      { level: "L5", text: "Greater than 98%: AI agents maintain allocation continuously; real-time reconciliation with the GL; rare exceptions flagged and explained automatically" },
    ],
  },
  {
    code: "D3",
    label: "Service orientation",
    sub: "Catalog · tiering · costing · show-back · demand · pricing · disputes · self-service · storefront · owner enablement",
    text: {
      business:
        "Is there a clear, accessible list of IT services you can request or consume, written in business language, not technical jargon?",
      it: "Does your organisation have a formal IT service catalog?",
      finance:
        "Does a formal, financially-governed IT service catalog exist with defined ownership and cost accountability per service?",
      default: "Does your organisation have a formal IT service catalog?",
    },
    options: [
      { level: "L1", text: "No service catalog; IT communicates via internal orders, cost centres, or project codes only; catalog covers less than 40% of actual business and application needs" },
      { level: "L2", text: "An informal or partial service list exists; covers 40-60% of business needs; defined in silo by IT with limited business collaboration; not financially owned" },
      { level: "L3", text: "Formal catalog covers infrastructure and major applications (60-80% of business needs); service owners assigned; defined in closer collaboration with the business" },
      { level: "L4", text: "Comprehensive catalog covering 80-90% of business needs in business language; digitised and accessible to consumers; curated 3rd-party and hyperscaler services included and easy to provision" },
      { level: "L5", text: "Self-service catalog with automated ordering covering >90% of needs; AI agents continuously monitor 3rd-party and hyperscaler marketplaces and propose catalog additions; definitions co-evolve with business in real time" },
    ],
  },
  {
    code: "D4",
    label: "Financial planning",
    sub: "Budgeting · forecast accuracy · RtB/CtB split · reforecast cadence · investment decisions",
    text: {
      business:
        "How often does IT's financial plan get meaningfully refreshed: annual budget only, mid-year reforecast, quarterly rolling, monthly rolling, or always current?",
      it: "How would you describe your IT budgeting and reforecasting cycle?",
      finance:
        "How frequently is IT's financial plan formally refreshed: annually, semi-annually, quarterly, monthly, or continuously?",
      default: "How would you describe your IT budgeting and reforecasting cycle?",
    },
    options: [
      { level: "L1", text: "Annual budget set top-down; any change requires weeks of approval; no rolling view; technical capacity added ad-hoc with no link to financial plan" },
      { level: "L2", text: "Annual budget with one mid-year reforecast; Finance leads; cycle takes 30-45 days; capacity forecasting based on historical growth only with no business input" },
      { level: "L3", text: "12-month rolling forecast updated quarterly; IT and Finance co-own; cycle 15-30 days; business input collected for capacity planning on major projects" },
      { level: "L4", text: "Continuous rolling forecast refreshed monthly; driven by service demand signals; cycle 15 days or less; technical capacity and financial forecast linked with business representatives actively involved" },
      { level: "L5", text: "Always-on financial intelligence; no discrete budget season; AI agents continuously adjust the plan in response to demand and market signals; demand forecast and financial forecast fully integrated; board-ready view live at any moment" },
    ],
  },
  {
    code: "D5",
    label: "Operating model",
    sub: "Governance · service owners · cadence · BU engagement · alignment · reporting · integrated views · accountability · coordination",
    text: {
      business:
        "Is there a clear governance structure that gives you a voice in IT financial decisions, or does IT simply send you a bill with no mechanism to challenge it?",
      it: "How is cost-transparency governance structured in your organisation?",
      finance:
        "How formally is cost-transparency governance structured, and how well is it integrated into corporate governance and investment decision processes?",
      default: "How is cost-transparency governance structured in your organisation?",
    },
    options: [
      { level: "L1", text: "No formal governance; cost management is owned informally by one team with no steering mechanism" },
      { level: "L2", text: "Basic governance agreed; a sponsor identified; initiative has a home but limited formal structure" },
      { level: "L3", text: "Formal governance established; steering group with IT, Finance, and Business; sign-off process in place" },
      { level: "L4", text: "Governance integrated into corporate governance; technology investments reviewed through a cost-transparency lens as standard" },
      { level: "L5", text: "Dedicated centre of excellence operational; AI agents continuously surface investment and cost trade-offs to the governance body; external benchmarking active" },
    ],
  },
  {
    code: "D6",
    label: "Business value",
    sub: "Strategic traceability · benchmarking · optimisation · ROI · investment decisions · cost-management as investment",
    text: {
      business:
        "Can IT demonstrate a clear, credible link between its spending and your business unit's specific strategic goals and outcomes?",
      it:
        "What percentage of IT spend can be traced to a specific strategic initiative, business capability, or value driver?",
      finance:
        "What proportion of IT expenditure is formally linked to strategic initiatives, business capabilities, or measurable value drivers?",
      default:
        "What percentage of IT spend can be traced to a specific strategic initiative, business capability, or value driver?",
    },
    options: [
      { level: "L1", text: "Less than 10%: almost no connection between IT spend and strategic priorities" },
      { level: "L2", text: "10-20%: broad alignment (e.g. innovation budget) but not validated at individual spend level" },
      { level: "L3", text: "30-55%: major investments mapped to business capabilities; portfolio segmented by strategic intent" },
      { level: "L4", text: "55-80%: most IT spend traceable to a strategic initiative; actively used to prioritise decisions" },
      { level: "L5", text: "Greater than 80%: near-complete traceability; AI agents maintain a live line-of-sight from every material spend item to a strategic owner and business case" },
    ],
  },
  {
    code: "D7",
    label: "Language & culture",
    sub: "Financial literacy · shared vocabulary · dialogue · education · collaboration",
    text: {
      business:
        "When IT leaders talk about costs and value, do they speak in language you can understand and challenge, or do conversations quickly become impenetrable?",
      it: "How financially literate are your IT leaders and their teams?",
      finance:
        "How fluent are IT leaders in financial management concepts, accounting principles, and business value language?",
      default: "How financially literate are your IT leaders and their teams?",
    },
    options: [
      { level: "L1", text: "IT staff have no financial literacy; financial concepts are unfamiliar across the technology function" },
      { level: "L2", text: "IT leaders have access to basic financial knowledge but financial literacy is not formally developed or consistent" },
      { level: "L3", text: "IT leaders and teams have access to educational resources covering financial and business concepts; the cost-management team stays current" },
      { level: "L4", text: "Stakeholders across IT, Finance, and Business have access to cross-domain education; IT leaders are financially fluent" },
      { level: "L5", text: "Organisation promotes continuous learning; IT and Business leaders understand each other's domains deeply in every decision" },
    ],
  },
  {
    code: "D8",
    label: "AI & agents",
    sub: "AI in data · allocation · forecasting · optimisation · AI spend visibility · AI unit economics · AI investment governance",
    text: {
      business:
        "When IT enriches, classifies, or reconciles the cost data that eventually reaches you, is that work done by AI agents working continuously, or by people spending weeks in spreadsheets?",
      it:
        "To what extent do AI agents handle the enrichment, classification, tagging, and reconciliation of cost and consumption data across your sources?",
      finance:
        "How far has your organisation progressed in using AI agents to automate data enrichment, classification, reconciliation, and exception handling in the cost-management pipeline?",
      default:
        "To what extent do AI agents handle enrichment, classification, and reconciliation of cost and consumption data?",
    },
    options: [
      { level: "L1", text: "No AI involvement in data work; all enrichment, classification, and reconciliation done manually or via brittle rules maintained by people" },
      { level: "L2", text: "Limited experimentation with AI for data tasks (e.g. a pilot on one source) but no production use; humans still do the real work" },
      { level: "L3", text: "AI assists with specific data tasks in production: e.g. auto-classifying untagged spend or flagging anomalies: with humans reviewing every output" },
      { level: "L4", text: "AI agents routinely enrich, classify, and reconcile data across most sources; humans handle exceptions and edge cases; clear measurable time-saving vs. manual baseline" },
      { level: "L5", text: "AI agents own the data pipeline end-to-end: continuously enriching, classifying, reconciling, and self-correcting; humans audit outcomes rather than doing the work; data is always-fresh" },
    ],
  },
];
